import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';

export interface DiretoriaFaturamentoProdutoDetalheModalProps {
  open: boolean;
  onClose: () => void;
  produto: string | null;
  companyId?: string;
  periodFrom: string;
  periodTo: string;
}

interface FaturamentoProdutoDetalhePayload {
  produto: string;
  periodo: { dataFrom: string; dataTo: string };
  total: number;
  totalPago: number;
  items: Array<{
    idLocacao: string;
    moto: string;
    placa: string;
    dataInicio: string | null;
    dataPrevistaTermino: string | null;
    valorFaturado: number;
    cliente: string;
    empresa: string;
  }>;
  querySql?: string;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fmtPeriodo(from: string, to: string): string {
  return `${fmtDate(from)} → ${fmtDate(to)}`;
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number.isFinite(n) ? n : 0
  );
}

export const DiretoriaFaturamentoProdutoDetalheModal: React.FC<
  DiretoriaFaturamentoProdutoDetalheModalProps
> = ({ open, onClose, produto, companyId, periodFrom, periodTo }) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['diretoria-faturamento-produto-detalhe', produto, companyId, periodFrom, periodTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('produto', produto!);
      if (companyId) params.set('companyId', companyId);
      if (periodFrom) params.set('dataFrom', periodFrom);
      if (periodTo) params.set('dataTo', periodTo);
      const res = await api.get(`/reports/diretoria/faturamento-produto-detalhe?${params.toString()}`);
      return res.data.data as FaturamentoProdutoDetalhePayload;
    },
    enabled: open && Boolean(produto),
    staleTime: 60_000,
    retry: 1,
  });

  if (!open || !produto) return null;

  const errMsg =
    isError && error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: string }).message ?? 'Falha ao carregar')
      : isError
        ? 'Falha ao carregar detalhe do produto'
        : null;

  const periodoLabel = data?.periodo
    ? fmtPeriodo(data.periodo.dataFrom, data.periodo.dataTo)
    : fmtPeriodo(periodFrom, periodTo);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Fechar" onClick={onClose} />
      <Card className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden border-border shadow-lg">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 border-b border-border/80 pb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <CardTitle className="min-w-0 flex-1 text-lg break-words">{produto}</CardTitle>
              <DiretoriaSqlButton
                sql={data?.querySql}
                source="cobranca"
                title={`${produto} — SQL detalhe`}
                className="mt-1 shrink-0"
                stopPropagation
              />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Locações com faturamento no período
              {data?.total != null ? ` · ${data.total.toLocaleString('pt-BR')} registro(s)` : ''}
              {data?.totalPago != null ? ` · ${fmtCurrency(data.totalPago)}` : ''}
              {` · ${periodoLabel}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-4">
          {isPending ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Carregando locações…</p>
          ) : errMsg ? (
            <p className="py-10 text-center text-sm text-rose-700">{errMsg}</p>
          ) : (data?.items.length ?? 0) === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Nenhuma locação encontrada para este produto no período.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/80">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left text-muted-foreground">
                    <th className="px-2 py-1.5 font-medium">Locação</th>
                    <th className="px-2 py-1.5 font-medium">Moto</th>
                    <th className="px-2 py-1.5 font-medium">Placa</th>
                    <th className="px-2 py-1.5 font-medium">Início</th>
                    <th className="px-2 py-1.5 font-medium">Prev. término</th>
                    <th className="px-2 py-1.5 font-medium text-right">Valor faturado</th>
                    <th className="px-2 py-1.5 font-medium">Cliente</th>
                    <th className="px-2 py-1.5 font-medium">Empresa</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((row) => (
                    <tr
                      key={`${row.idLocacao}-${row.placa}-${row.empresa}`}
                      className="border-b border-border/50 last:border-0"
                    >
                      <td className="px-2 py-1.5 tabular-nums font-medium">{row.idLocacao}</td>
                      <td className="px-2 py-1.5 max-w-[10rem] truncate" title={row.moto}>
                        {row.moto}
                      </td>
                      <td className="px-2 py-1.5 tabular-nums">{row.placa}</td>
                      <td className="px-2 py-1.5 tabular-nums">{fmtDate(row.dataInicio)}</td>
                      <td className="px-2 py-1.5 tabular-nums">{fmtDate(row.dataPrevistaTermino)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {fmtCurrency(row.valorFaturado)}
                      </td>
                      <td className="px-2 py-1.5 max-w-[12rem] truncate" title={row.cliente}>
                        {row.cliente}
                      </td>
                      <td className="px-2 py-1.5 max-w-[10rem] truncate" title={row.empresa}>
                        {row.empresa}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
